/**
 * 账户（Auth）抽象（阶段 4 / ADR-003）。
 *
 * 两种实现：
 *   - SupabaseAuthProvider：真实托管登录（邮箱+密码）。要求配置
 *     VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY（公共信息，可进客户端包）；未配置时
 *     明确降级为本地模式（见 LocalAuthProvider），绝不伪造真实账户。
 *   - LocalAuthProvider：无后端环境下的单设备本地身份（deviceId 固定），
 *     仅用于离线/未配置时继续使用全部本地能力；UI 必须如实标注「本地模式」。
 */

import { createClient, type Session, type SupabaseClient } from "@supabase/supabase-js";

export interface AuthUserInfo {
  id: string;
  email: string | null;
  isLocal: boolean;
}

export interface AuthProvider {
  readonly kind: "supabase" | "local";
  readonly configured: boolean;
  getCurrentUser(): Promise<AuthUserInfo | null>;
  signIn(email: string, password: string): Promise<AuthUserInfo>;
  signUp(email: string, password: string): Promise<AuthUserInfo>;
  signOut(): Promise<void>;
  /** 获取一个已登录的 Supabase Session（本地模式返回 null） */
  getSession(): Promise<Session | null>;
}

/** 环境是否配置了 Supabase 公共端点。 */
export function isSupabaseConfigured(): boolean {
  const url = import.meta.env.VITE_SUPABASE_URL as string | undefined;
  const anon = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;
  return Boolean(url && url.startsWith("https://") && anon && anon.length > 20);
}

export const LOCAL_USER: AuthUserInfo = { id: "local:user-demo", email: null, isLocal: true };
export const LOCAL_DEVICE_ID = "device-local-1";

/** 本地模式（未配置 Supabase 时的诚实降级，UI 必须显示「本地模式」）。 */
export class LocalAuthProvider implements AuthProvider {
  readonly kind = "local" as const;
  readonly configured = false;
  async getCurrentUser(): Promise<AuthUserInfo> {
    return LOCAL_USER;
  }
  async signIn(): Promise<AuthUserInfo> {
    return LOCAL_USER;
  }
  async signUp(): Promise<AuthUserInfo> {
    return LOCAL_USER;
  }
  async signOut(): Promise<void> {
    /* 本地模式无可退出会话 */
  }
  async getSession(): Promise<Session | null> {
    return null;
  }
}

/** Supabase 账户实现（邮箱+密码；ADR-003）。 */
export class SupabaseAuthProvider implements AuthProvider {
  readonly kind = "supabase" as const;
  readonly configured = true;
  private readonly client: SupabaseClient;

  constructor() {
    const url = import.meta.env.VITE_SUPABASE_URL as string;
    const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;
    // 构造函数仅在配置齐全时被调用（createAuthProvider 保证）
    this.client = createClient(url, anonKey, {
      auth: { persistSession: true, autoRefreshToken: true },
    });
  }

  async getCurrentUser(): Promise<AuthUserInfo | null> {
    const { data } = await this.client.auth.getUser();
    const u = data.user;
    if (!u) return null;
    return { id: u.id, email: u.email ?? null, isLocal: false };
  }

  async signIn(email: string, password: string): Promise<AuthUserInfo> {
    const { data, error } = await this.client.auth.signInWithPassword({ email, password });
    if (error || !data.user) throw new AuthError(error?.message ?? "登录失败");
    return { id: data.user.id, email: data.user.email ?? null, isLocal: false };
  }

  async signUp(email: string, password: string): Promise<AuthUserInfo> {
    const { data, error } = await this.client.auth.signUp({ email, password });
    if (error || !data.user) throw new AuthError(error?.message ?? "注册失败");
    return { id: data.user.id, email: data.user.email ?? null, isLocal: false };
  }

  async signOut(): Promise<void> {
    await this.client.auth.signOut();
  }

  async getSession(): Promise<Session | null> {
    const { data } = await this.client.auth.getSession();
    return data.session;
  }

  /** 获取带 JWT 头调 Edge Function 用的 Supabase client。 */
  getClient(): SupabaseClient {
    return this.client;
  }
}

export class AuthError extends Error {}

/** 工厂：按环境配置选择实现（未配置 → 本地模式，UI 如实提示）。 */
export function createAuthProvider(): AuthProvider {
  return isSupabaseConfigured() ? new SupabaseAuthProvider() : new LocalAuthProvider();
}